import { useState, useEffect, useRef } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { authClient } from '../../utils/amplifyClient';
import * as Sentry from '@sentry/react';
import SignaturePad from 'signature_pad';
import { v4 as uuidv4 } from 'uuid';
import '../../css/TrainingQuizWizard.css';
import { createTrainingQuizResponse } from '../../graphql/trainingQueries';
import { base64ToBlob } from '../../utils/helperFunctions';
import { S3_IMAGE_PATH, ITEM_STATUS_CODES } from '../../utils/constants';
import { PlanCategory, TrainingQuizResponse } from '../../types/trainingTypes';
import { BackButton } from '../ui';

interface QuizPrompt {
    type: string;
    component: string;
    description: string;
    options: string[];
    correctAnswer?: string;
}

interface QuizAnswer {
    prompt: string;
    userAnswer: string;
    correctAnswer: string;
    isCorrect: boolean;
}

interface TrainingQuizWizardProps {
    category: PlanCategory;
    companyId: string;
    userId: string;
    planId: string;
    cycleStartDate: string;
    existingAttempts: TrainingQuizResponse[];
    onComplete: (passed: boolean) => void;
    onCancel: () => void;
}

export const TrainingQuizWizard = ({
    category,
    companyId,
    userId,
    planId,
    cycleStartDate,
    existingAttempts,
    onComplete,
    onCancel,
}: TrainingQuizWizardProps) => {

    const [isLoading, setIsLoading] = useState(true);
    const [questions, setQuestions] = useState<QuizPrompt[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<{ [idx: number]: string }>({});
    const [showReview, setShowReview] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [signatureCaptured, setSignatureCaptured] = useState(false);
    const [signaturePad, setSignaturePad] = useState<SignaturePad | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Load quiz form
    useEffect(() => {
        loadQuizForm();
    }, []);

    // Init signature pad when review is shown
    useEffect(() => {
        if (showReview && canvasRef.current) {
            const canvas = canvasRef.current;
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext('2d')?.scale(ratio, ratio);

            const pad = new SignaturePad(canvas, {
                minWidth: 1,
                maxWidth: 2.5,
                penColor: 'rgb(66, 133, 244)',
                backgroundColor: 'rgb(255, 255, 255)',
            });
            setSignaturePad(pad);
        }
    }, [showReview]);

    const loadQuizForm = async () => {
        if (!category.quizFormId) {
            onCancel();
            return;
        }

        try {
            const result = await authClient.graphql({
                query: /* GraphQL */ `query GetForm($id: ID!) { getForm(id: $id) { id name formData } }`,
                variables: { id: category.quizFormId },
            });
            const form = (result as any).data.getForm;
            if (!form?.formData) {
                onCancel();
                return;
            }

            const parsed = JSON.parse(form.formData);
            const prompts: QuizPrompt[] = [];
            parsed.forEach((section: any) => {
                if (section.type === 'section' && section.prompts) {
                    section.prompts.forEach((p: any) => {
                        prompts.push({
                            type: p.type || 'yesNo',
                            component: p.component || '',
                            description: p.description || '',
                            options: p.options || [],
                            correctAnswer: p.correctAnswer || '',
                        });
                    });
                }
            });

            setQuestions(prompts);
        } catch (error) {
            console.error('Error loading quiz form:', error);
            Sentry.captureException(error);
            onCancel();
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnswer = (value: string) => {
        setAnswers(prev => ({ ...prev, [currentIndex]: value }));
    };

    const goNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setShowReview(true);
        }
    };

    const goBack = () => {
        if (showReview) {
            setShowReview(false);
        } else if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const gradeQuiz = (): QuizAnswer[] => {
        return questions.map((q, idx) => {
            const userAnswer = answers[idx] || '';
            const correctAnswer = q.correctAnswer || '';

            let isCorrect: boolean;
            if (q.type === 'checkbox') {
                // For checkbox: compare as sets (order-independent, case-insensitive)
                const userSet = new Set(userAnswer.split(',').map(s => s.trim().toLowerCase()).filter(Boolean));
                const correctSet = new Set(correctAnswer.split(',').map(s => s.trim().toLowerCase()).filter(Boolean));
                isCorrect = userSet.size === correctSet.size && [...userSet].every(v => correctSet.has(v));
            } else {
                // For yes/no and multiple choice: case-insensitive trim comparison
                isCorrect = userAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
            }

            return {
                prompt: q.component,
                userAnswer,
                correctAnswer,
                isCorrect,
            };
        });
    };

    const handleSubmit = async () => {
        if (!signaturePad || signaturePad.isEmpty()) return;
        setIsSaving(true);

        try {
            // Upload signature
            let signaturePath = '';
            const signatureData = signaturePad.toDataURL('image/jpeg', 0.8);
            const blob = base64ToBlob(signatureData, 'image/jpeg');
            if (blob && navigator.onLine) {
                const imageName = `${companyId}/${uuidv4()}-QUIZ-SIGNATURE-${category.name}.jpeg`;
                const imageFile = new File([blob], imageName, { type: 'image/jpeg' });
                const savedImage = await uploadData({
                    path: S3_IMAGE_PATH.signatureImagePath(companyId, imageName),
                    data: imageFile,
                }).result;
                signaturePath = savedImage.path;
            }

            // Grade
            const graded = gradeQuiz();
            const score = graded.filter(a => a.isCorrect).length;
            const totalQuestions = graded.length;
            const passed = score === totalQuestions;
            const attemptNumber = existingAttempts.length + 1;

            // Save response
            await createTrainingQuizResponse({
                companyId,
                userId,
                planId,
                categoryId: category.id,
                formId: category.quizFormId!,
                responseData: JSON.stringify(graded),
                signaturePath,
                passed,
                score,
                totalQuestions,
                attemptNumber,
                completedAt: new Date().toISOString(),
                cycleStartDate,
            });

            onComplete(passed);
        } catch (error) {
            console.error('Error submitting quiz:', error);
            Sentry.captureException(error);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="quiz-wizard-container" style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: '#64748b', fontSize: 14 }}>Loading quiz...</p>
            </div>
        );
    }

    if (questions.length === 0) {
        return (
            <div className="quiz-wizard-container" style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: '#64748b', fontSize: 14 }}>No questions found for this quiz.</p>
            </div>
        );
    }

    // Review & Sign screen
    if (showReview) {
        const graded = gradeQuiz();
        const score = graded.filter(a => a.isCorrect).length;
        const allCorrect = score === graded.length;

        return (
            <div className="quiz-wizard-container quiz-review-container">
                <h2>Quiz Review: {category.name}</h2>

                <div className={`quiz-review-score ${allCorrect ? 'passed' : 'failed'}`}>
                    Score: {score} / {graded.length}
                    {allCorrect ? ' — Passed! ✅' : ' — Not all correct ❌'}
                </div>

                <table className="quiz-review-table">
                    <thead>
                        <tr>
                            <th>Question</th>
                            <th>Your Answer</th>
                            <th>Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {graded.map((item, idx) => (
                            <tr key={idx} className={item.isCorrect ? 'correct' : 'incorrect'}>
                                <td>{item.prompt}</td>
                                <td>{item.userAnswer || '(no answer)'}</td>
                                <td>
                                    {item.isCorrect ? '✅' : `❌ (Correct: ${item.correctAnswer})`}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="quiz-signature-section">
                    <h3>Signature</h3>
                    <p>Sign below to confirm your quiz submission.</p>
                    <canvas
                        ref={canvasRef}
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            width: '100%',
                            maxWidth: 500,
                            height: 200,
                            margin: '16px 0',
                            background: '#fff',
                            display: 'block',
                        }}
                    />
                    <div className="quiz-signature-actions">
                        <button
                            className="quiz-btn quiz-btn-secondary"
                            onClick={() => { signaturePad?.clear(); setSignatureCaptured(false); }}
                        >
                            Clear
                        </button>
                        <button
                            className="quiz-btn quiz-btn-primary"
                            disabled={signatureCaptured}
                            onClick={() => { if (signaturePad && !signaturePad.isEmpty()) setSignatureCaptured(true); }}
                        >
                            {signatureCaptured ? 'Saved ✓' : 'Save Signature'}
                        </button>
                    </div>
                </div>

                <div className="quiz-wizard-actions">
                    <BackButton
                        onClick={goBack}
                        buttonText="Back to Questions"
                        buttonClasses="back-button--inline"
                    />
                    <button
                        className="quiz-btn quiz-btn-primary"
                        onClick={handleSubmit}
                        disabled={!signatureCaptured || isSaving}
                    >
                        {isSaving ? 'Submitting...' : 'Submit Quiz'}
                    </button>
                </div>
            </div>
        );
    }

    // Question screen
    const q = questions[currentIndex];
    const currentAnswer = answers[currentIndex] || '';

    return (
        <div className="quiz-wizard-container">
            <h2>Quiz: {category.name}</h2>

            <div className="quiz-wizard-progress">
                <div className="quiz-wizard-progress-bar">
                    <div
                        className="quiz-wizard-progress-fill"
                        style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                    />
                </div>
                <div className="quiz-wizard-progress-text">
                    Question {currentIndex + 1} of {questions.length}
                </div>
            </div>

            <div className="quiz-wizard-question-card">
                <h3 className="quiz-wizard-question-title">{q.component}</h3>
                {q.description && <p className="quiz-wizard-question-desc">{q.description}</p>}

                <div className="quiz-wizard-answers">
                    {q.type === 'yesNo' && (
                        <>
                            <button
                                className={`quiz-answer-btn yes ${currentAnswer === 'Yes' ? 'selected' : ''}`}
                                onClick={() => handleAnswer('Yes')}
                            >
                                YES
                            </button>
                            <button
                                className={`quiz-answer-btn no ${currentAnswer === 'No' ? 'selected' : ''}`}
                                onClick={() => handleAnswer('No')}
                            >
                                NO
                            </button>
                        </>
                    )}

                    {q.type === 'dropdown' && (
                        <select
                            value={currentAnswer}
                            onChange={e => handleAnswer(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: 8,
                                border: '2px solid #e2e8f0',
                                fontSize: 14,
                                color: '#1e293b',
                                background: '#fff',
                            }}
                        >
                            <option value="">Select an answer</option>
                            {q.options.map((o, i) => (
                                <option key={i} value={o}>{o}</option>
                            ))}
                        </select>
                    )}

                    {q.type === 'checkbox' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                            {q.options.map((option, idx) => {
                                const currentArr = currentAnswer ? currentAnswer.split(',').filter(Boolean) : [];
                                const isChecked = currentArr.includes(option);
                                return (
                                    <label
                                        key={idx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '10px 14px',
                                            borderRadius: 8,
                                            border: isChecked ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                                            background: isChecked ? '#eff6ff' : '#fff',
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            color: '#1e293b',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={e => {
                                                const updated = e.target.checked
                                                    ? [...currentArr, option]
                                                    : currentArr.filter(v => v !== option);
                                                handleAnswer(updated.join(','));
                                            }}
                                            style={{ width: 16, height: 16 }}
                                        />
                                        {option}
                                    </label>
                                );
                            })}
                        </div>
                    )}

                    {q.type === 'sectionQuestion' && (
                        <input
                            type="text"
                            value={currentAnswer}
                            onChange={e => handleAnswer(e.target.value)}
                            placeholder="Type your answer..."
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: 8,
                                border: '2px solid #e2e8f0',
                                fontSize: 14,
                                color: '#1e293b',
                                boxSizing: 'border-box',
                            }}
                        />
                    )}

                    {q.type === 'fourResponse' && (
                        <>
                            {[
                                { label: 'OK', value: ITEM_STATUS_CODES.ok },
                                { label: 'Warning', value: ITEM_STATUS_CODES.warn },
                                { label: 'Inop', value: ITEM_STATUS_CODES.inop },
                                { label: 'N/A', value: ITEM_STATUS_CODES.na },
                            ].map(opt => (
                                <button
                                    key={opt.value}
                                    className={`quiz-answer-btn ${currentAnswer === opt.value ? 'selected' : ''}`}
                                    onClick={() => handleAnswer(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </>
                    )}                </div>
            </div>

            <div className="quiz-wizard-actions">
                {currentIndex > 0 && (
                    <button className="quiz-btn quiz-btn-secondary" onClick={goBack}>Previous</button>
                )}
                <button className="quiz-btn quiz-btn-cancel" onClick={onCancel}>Cancel</button>
                <button
                    className="quiz-btn quiz-btn-primary"
                    disabled={!currentAnswer}
                    onClick={goNext}
                >
                    {currentIndex === questions.length - 1 ? 'Review & Sign' : 'Next'}
                </button>
            </div>
        </div>
    );
};
